import { jsx as _jsx } from "react/jsx-runtime";
import React, { Fragment, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { DateInput, Input, Select, useMultiSelect, } from 'ziphy-web-shared/basic/lib/forms/fields';
import { ConnectedGroup, ToggleButton } from 'ziphy-web-shared/basic/lib/simple';
import styles from './ToggleButtonGroup.module.scss';
const ToggleButtonGroup = ({ field, options, 
//
multi = false, allowUnselect = true, disabled = false, isExtraFieldRequired = false, 
//
renderOption, renderOptionProps, size = 'sm', className, connectedGroupClassName, }) => {
    const { value, onChange, isInvalid } = field;
    const multiSelect = useMultiSelect({
        value,
        onChange,
        isInvalid,
        multi,
        allowUnselect,
        disabled,
    });
    const openedEmptyExtraFields = useMemo(() => {
        const result = [];
        if (!isExtraFieldRequired)
            return result;
        options.forEach((option) => {
            var _a;
            if (typeof option.extraField === 'object' &&
                option.extraField &&
                multiSelect.selectedOptions.has(option.id)) {
                const currentExtraValue = ((_a = multiSelect.selectedOptions.get(option.id)) === null || _a === void 0 ? void 0 : _a.extra) || '';
                if (!currentExtraValue)
                    result.push(option.id);
            }
        });
        return result;
    }, [isInvalid, value, options]);
    const renderToggleButton = (option, extraClassName) => {
        const effectiveOption = multiSelect.getEffectiveOption(option);
        const props = {
            option: effectiveOption,
            onToggle: () => multiSelect.handleToggleSelect(option),
            isSelected: multiSelect.selectedOptions.has(option.id),
            className: extraClassName,
        };
        if (typeof renderOption === 'function') {
            return renderOption(props);
        }
        const extraProps = typeof renderOptionProps === 'function' ? renderOptionProps(props) : {};
        return (_jsx(ToggleButton, Object.assign({ mode: (renderOption === null || renderOption === void 0 ? void 0 : renderOption.mode) || ['secondary-dark', size], checkbox: Object.assign({ multi }, ((renderOption === null || renderOption === void 0 ? void 0 : renderOption.checkbox) || {})), selected: props.isSelected, disabled: effectiveOption.disabled, invalid: effectiveOption.invalid && isExtraFieldRequired && openedEmptyExtraFields.length
                ? openedEmptyExtraFields.includes(option.id)
                : effectiveOption.invalid, label: effectiveOption.text, onClick: props.onToggle, className: props.className }, extraProps)));
    };
    const renderExtraField = (option, extraClassName) => {
        var _a;
        const effectiveOption = multiSelect.getEffectiveOption(option);
        const currentExtraValue = ((_a = multiSelect.selectedOptions.get(option.id)) === null || _a === void 0 ? void 0 : _a.extra) || '';
        const fieldProps = {
            value: currentExtraValue,
            onChange: (val) => multiSelect.handleExtraFieldChange(option, val),
            isInvalid: effectiveOption.invalid,
        };
        if (typeof option.extraField === 'function') {
            return option.extraField({
                value: currentExtraValue,
                onChange: fieldProps.onChange,
                option,
                className: extraClassName,
            });
        }
        const fieldConfig = typeof option.extraField === 'object' && option.extraField;
        const isInvalid = effectiveOption.invalid && isExtraFieldRequired && openedEmptyExtraFields.length
            ? openedEmptyExtraFields.includes(option.id)
            : effectiveOption.invalid;
        if ((fieldConfig === null || fieldConfig === void 0 ? void 0 : fieldConfig.element) === 'select') {
            return (_jsx(Select, Object.assign({ field: {
                    value: currentExtraValue,
                    onChange: (val) => fieldProps.onChange(val),
                    isInvalid,
                } }, fieldConfig, { mode: (fieldConfig === null || fieldConfig === void 0 ? void 0 : fieldConfig.mode) || [size], disabled: effectiveOption.disabled, innerClassName: extraClassName })));
        }
        if ((fieldConfig === null || fieldConfig === void 0 ? void 0 : fieldConfig.element) === 'date') {
            return (_jsx(DateInput, Object.assign({ field: {
                    value: currentExtraValue,
                    onChange: (val) => fieldProps.onChange(val),
                    isInvalid,
                } }, fieldConfig, { mode: (fieldConfig === null || fieldConfig === void 0 ? void 0 : fieldConfig.mode) || [size], disabled: effectiveOption.disabled, innerClassName: extraClassName })));
        }
        return (_jsx(Input, Object.assign({ field: {
                value: currentExtraValue,
                onChange: fieldProps.onChange,
                isInvalid: isInvalid,
            } }, fieldConfig, { mode: (fieldConfig === null || fieldConfig === void 0 ? void 0 : fieldConfig.mode) || [size], disabled: effectiveOption.disabled, innerClassName: extraClassName })));
    };
    return (_jsx("div", Object.assign({ className: classNames(styles.wrapper, className) }, { children: options.map((option) => multiSelect.hasExtraField(option) ? (_jsx(ConnectedGroup, { 
            // wrap={true}
            styleSet: ['resetCorners'], renderItems: [
                ({ getClass }) => {
                    return renderToggleButton(option, classNames(getClass(), styles.connectedGroupButton));
                },
                ({ getClass }) => {
                    return renderExtraField(option, classNames(getClass(), styles.connectedExtraField));
                },
            ], className: classNames(styles.connectedGroup, connectedGroupClassName) }, option.id)) : (_jsx(Fragment, { children: renderToggleButton(option) }, option.id))) })));
};
export default observer(ToggleButtonGroup);
